import React from 'react';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format, isValid } from 'date-fns';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Drawer, DrawerContent, DrawerTrigger } from '../ui/drawer';
import { updateField } from '@flysync/shared-redux/slices/flightSearchSlice';
import { useDispatch } from 'react-redux';

// Helper function to safely format dates
const safeFormatDate = (date: Date, formatString: string): string => {
  try {
    if (!date || !isValid(date)) return '';
    return format(date, formatString);
  } catch (error) {
    console.warn('Date formatting error:', error);
    return '';
  }
};

interface DateI {
  title: string;
  disabled: boolean;
  className?: string;
  type?: 'FromDate' | 'ToDate';
  children: React.ReactNode;
  setDate: (date: Date) => void;
  selectedDate: Date | null;
  disableDates: { min: Date; max: Date };
  isChangeMonth?: boolean;
}
function DatePicker({
  disabled,
  type,
  children,
  setDate,
  className,
  selectedDate,
  disableDates,
  isChangeMonth = false,
}: DateI) {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const dispatch = useDispatch();
  const handleOpenChange = (open: boolean) => {
    if (!disabled) {
      setIsOpen(open);
    }
  };

  const onSelect = (dateInput: Date) => {
    if (dateInput && isValid(dateInput)) {
      setDate(dateInput);
      if (type === 'FromDate') {
        dispatch(
          updateField({
            field: 'date',
            value: safeFormatDate(dateInput, 'yyyy-MM-dd'),
          }),
        );
      } else if (type === 'ToDate') {
        dispatch(
          updateField({
            field: 'rt_date',
            value: safeFormatDate(dateInput, 'yyyy-MM-dd'),
          }),
        );
      }
    }

    setIsOpen(false);
  };
  if (isDesktop) {
    return (
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild disabled={disabled} className={className}>
          <div>{children}</div>
        </PopoverTrigger>
        {!disabled && (
          <PopoverContent side="bottom" align="start" className="bg-tertiary-light w-fit p-0 max-h-[100px] side-bottom">
            <Calendar
              mode="single"
              selected={selectedDate ? new Date(selectedDate) : undefined}
              onDayClick={(date) => onSelect(date as Date)}
              onSelect={(date) => onSelect(date as Date)}
              initialFocus
              minDate={disableDates?.min}
              maxDate={disableDates?.max}
            />
          </PopoverContent>
        )}
      </Popover>
    );
  }
  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild disabled={disabled} className={className}>
        <div>{children}</div>
      </DrawerTrigger>
      {!disabled && (
        <DrawerContent className="items-center justify-center">
          <div className="mt-4 border-t">
            <Calendar
              mode="single"
              selected={selectedDate ? new Date(selectedDate) : undefined}
              onDayClick={(date) => onSelect(date as Date)}
              onSelect={(date) => onSelect(date as Date)}
              initialFocus
              minDate={disableDates?.min}
              maxDate={disableDates?.max}
              isChangeMonth={isChangeMonth}
            />
          </div>
        </DrawerContent>
      )}
    </Drawer>
  );
}
export default DatePicker;
